/**
 * User Profile Page
 * Edit user profile settings and manage invites
 */
import React, { useState, useEffect } from 'react';
import { Box, Heading, VStack, HStack, Button, Avatar, Card, CardHeader, CardBody, FormControl, FormLabel, Input, useToast, Text, Divider, Badge, Flex, Spinner, Select } from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/AuthService';
import { inviteService, type Invite } from '../services/InviteService';
import InviteList from '../components/invites/InviteList';
import GenerateInviteModal from '../components/invites/GenerateInviteModal';

export function ProfilePage() {
  const { user, refreshUser, isAuthenticated } = useAuth();
  const toast = useToast();

  // Profile state
  const [username, setUsername] = useState(user?.username || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Invites state
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 25,
    pages: 1,
  });
  const [availableCount, setAvailableCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'used'>('all');
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);

  // Load invites
  const loadInvites = async (params?: { page?: number; status?: string }) => {
    try {
      setLoading(true);
      const response = await inviteService.getMyInvites(
        statusFilter,
        pagination.page,
        pagination.limit
      );

      setInvites(response.invites);
      setPagination(response.pagination);
      setAvailableCount(response.available_count);
    } catch (error: any) {
      toast({
        title: 'Error loading invites',
        description: error.response?.data?.detail || 'Failed to load invites',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      loadInvites();
    }
  }, [isAuthenticated]);

  // Apply filter when it changes
  useEffect(() => {
    if (isAuthenticated) {
      loadInvites({ page: 1 });
    }
  }, [statusFilter]);

  if (!user) return null;

  const handleSave = async () => {
    if (!username.trim()) {
      toast({
        title: 'Invalid username',
        description: 'Username cannot be empty',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      await authService.updateProfile({ username: username.trim() });
      await refreshUser();

      toast({
        title: 'Profile updated',
        description: 'Your profile has been updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsEditing(false);
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error.response?.data?.detail || 'Failed to update profile',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setUsername(user.username);
    setIsEditing(false);
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, page: newPage });
    loadInvites({ page: newPage });
  };

  // Handle generate success
  const handleGenerateSuccess = () => {
    setIsGenerateModalOpen(false);
    loadInvites({ page: 1 });
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Profile Card */}
      <Card size="sm">
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <Heading size="sm">Personal Information</Heading>
            {!isEditing && (
              <Button onClick={() => setIsEditing(true)} colorScheme="blue" size="sm">
                Edit
              </Button>
            )}
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          <VStack spacing={3} align="stretch">
            {/* Avatar */}
            <HStack spacing={3}>
              <Avatar size="md" src={user.avatar} name={user.username} referrerPolicy="no-referrer" />
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold" fontSize="md">
                  {user.username}
                </Text>
                <Text color="gray.600" fontSize="xs">
                  {user.email}
                </Text>
                <Badge colorScheme={user.role === 'admin' ? 'purple' : 'blue'} fontSize="xs">
                  {user.role}
                </Badge>
              </VStack>
            </HStack>

            <Divider />

            {/* Editable Fields */}
            <FormControl>
              <FormLabel fontSize="sm">Username</FormLabel>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                isDisabled={!isEditing}
                size="sm"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Email</FormLabel>
              <Input value={user.email} isDisabled size="sm" />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Email cannot be changed
              </Text>
            </FormControl>

            {isEditing && (
              <HStack spacing={2}>
                <Button
                  onClick={handleSave}
                  isLoading={saving}
                  colorScheme="blue"
                  flex={1}
                  size="sm"
                >
                  Save
                </Button>
                <Button onClick={handleCancel} variant="outline" flex={1} size="sm">
                  Cancel
                </Button>
              </HStack>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Account Details */}
      <Card size="sm">
        <CardHeader pb={2}>
          <Heading size="sm">Account Details</Heading>
        </CardHeader>
        <CardBody pt={2}>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <Text fontWeight="medium" fontSize="sm">Account ID</Text>
              <Text color="gray.600" fontFamily="var(--font-ui-mono)" fontSize="xs">
                {user.id.substring(0, 8)}...
              </Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontWeight="medium" fontSize="sm">Member Since</Text>
              <Text color="gray.600" fontSize="xs">
                {new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontWeight="medium" fontSize="sm">Last Login</Text>
              <Text color="gray.600" fontSize="xs">
                {user.last_login
                  ? new Date(user.last_login).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Never'}
              </Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontWeight="medium" fontSize="sm">Available Invites</Text>
              <Badge colorScheme="green" fontSize="sm">
                {user.invites_available}
              </Badge>
            </HStack>

            <HStack justify="space-between">
              <Text fontWeight="medium" fontSize="sm">Account Status</Text>
              <Badge colorScheme={user.is_blocked ? 'red' : 'green'} fontSize="xs">
                {user.is_blocked ? 'Blocked' : 'Active'}
              </Badge>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Invites Section */}
      <Card size="sm">
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <Heading size="sm">My Invites</Heading>
            <Button
              leftIcon={<Plus size={14} />}
              onClick={() => setIsGenerateModalOpen(true)}
              size="xs"
              colorScheme="blue"
              isDisabled={user?.invites_available === 0 && user?.role !== 'admin'}
            >
              Generate
            </Button>
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          <VStack spacing={3} align="stretch">
            {/* Stats - Compact Grid */}
            <HStack spacing={2}>
              <Box bg="gray.50" p={2} borderRadius="md" flex={1}>
                <Text fontSize="xs" color="gray.600">
                  Available
                </Text>
                <Text fontSize="xl" fontWeight="bold">
                  {user?.role === 'admin' ? '∞' : user?.invites_available || 0}
                </Text>
              </Box>
              <Box bg="gray.50" p={2} borderRadius="md" flex={1}>
                <Text fontSize="xs" color="gray.600">
                  Total
                </Text>
                <Text fontSize="xl" fontWeight="bold">
                  {pagination.total}
                </Text>
              </Box>
              <Box bg="gray.50" p={2} borderRadius="md" flex={1}>
                <Text fontSize="xs" color="gray.600">
                  Active
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="blue.500">
                  {invites.filter((i) => i.is_active && !i.used_at).length}
                </Text>
              </Box>
              <Box bg="gray.50" p={2} borderRadius="md" flex={1}>
                <Text fontSize="xs" color="gray.600">
                  Used
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="green.500">
                  {invites.filter((i) => i.used_at).length}
                </Text>
              </Box>
            </HStack>

            {/* Filter */}
            <Box bg="gray.50" p={2} borderRadius="md">
              <HStack spacing={2}>
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  Filter:
                </Text>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  maxW="150px"
                  size="xs"
                >
                  <option value="all">All</option>
                  <option value="available">Available</option>
                  <option value="used">Used</option>
                </Select>
              </HStack>
            </Box>

            {/* Invites List */}
            <Box bg="white" borderRadius="md" border="1px solid" borderColor="gray.200" overflow="hidden">
              {loading ? (
                <Flex justify="center" align="center" py={10}>
                  <Spinner size="md" color="blue.500" />
                </Flex>
              ) : invites.length === 0 ? (
                <Flex justify="center" align="center" py={10} direction="column">
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    No invites yet
                  </Text>
                  {user?.invites_available && user.invites_available > 0 && (
                    <Text fontSize="xs" color="gray.400">
                      Click "Generate" to create your first invite
                    </Text>
                  )}
                </Flex>
              ) : (
                <>
                  <InviteList invites={invites} />

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <Flex justify="space-between" align="center" p={2} borderTop="1px" borderColor="gray.200">
                      <Text fontSize="xs" color="gray.600">
                        {(pagination.page - 1) * pagination.limit + 1}-
                        {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                      </Text>
                      <HStack spacing={1}>
                        <Button
                          size="xs"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          isDisabled={pagination.page === 1}
                        >
                          Prev
                        </Button>
                        {Array.from({ length: Math.min(pagination.pages, 3) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <Button
                              key={pageNum}
                              size="xs"
                              variant={pagination.page === pageNum ? 'solid' : 'outline'}
                              colorScheme={pagination.page === pageNum ? 'blue' : 'gray'}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                        <Button
                          size="xs"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          isDisabled={pagination.page === pagination.pages}
                        >
                          Next
                        </Button>
                      </HStack>
                    </Flex>
                  )}
                </>
              )}
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* Generate Invite Modal */}
      <GenerateInviteModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onSuccess={handleGenerateSuccess}
      />
    </VStack>
  );
}
